import React, { ReactNode, useCallback } from 'react';

import classNames from 'classnames/bind';

import { DropDownMenuItem } from '@components/DropDownMenuItem';
import { MenuItemType, TabProps } from '@components/MenuInsideModule';
import { ShowCategory } from '@modules/categories';

import styles from './MobileSubMenu.module.css';

const cn = classNames.bind(styles);

export interface MobileSubMenuProps {
  menuItems: Array<TabProps>;
  onSubMenuItemClick?: (menuItem: MenuItemType, to?: string) => void;
  onMenuItemClick?: (menuItem: TabProps, to?: string) => void;
  handleClose: () => void;
  selectedTab?: string | number | undefined;
  renderCustomMenuItem?: Nullable<(menuItem: TabProps) => ReactNode>;
}

export function MobileSubMenu({
  menuItems,
  onSubMenuItemClick,
  handleClose,
  onMenuItemClick,
  selectedTab,
  renderCustomMenuItem,
}: MobileSubMenuProps) {
  const handleCloseAndChoose = useCallback(
    (menuItem: TabProps) => {
      onMenuItemClick?.(menuItem);
      handleClose();
    },
    [onMenuItemClick, handleClose],
  );

  const handleSubMenuCloseAndChoose = useCallback(
    (menuItem: MenuItemType) => {
      onSubMenuItemClick?.(menuItem);
      handleClose();
    },
    [onSubMenuItemClick, handleClose],
  );

  const renderMenuItem = useCallback(
    (tab: TabProps, i: number) => {
      if (!tab.to) {
        return (
          <ShowCategory key={tab.id} category={tab.label || ''}>
            <DropDownMenuItem
              className={cn('mobile-submenu__item', {
                'mobile-submenu__item--active': selectedTab === tab.id,
              })}
              contentClassName={cn('mobile-submenu__item-content')}
              onClick={() => handleCloseAndChoose(tab)}
              badge={tab.badge}
              autoFocus={i === 0}
            >
              <span
                className={cn('mobile-submenu__item-inner', {
                  'mobile-submenu__item-inner--active': selectedTab === tab.id,
                })}
              >
                {tab.label}
              </span>
            </DropDownMenuItem>
          </ShowCategory>
        );
      }

      return (
        <ShowCategory key={tab.id} category={tab.label || ''}>
          <DropDownMenuItem
            className={cn('mobile-submenu__item', {
              'mobile-submenu__item--active': selectedTab === tab.id,
            })}
            contentClassName={cn('mobile-submenu__item-content')}
            onClick={() => handleCloseAndChoose(tab)}
            badge={tab.badge}
            autoFocus={i === 0}
            to={tab.to}
          >
            <span
              className={cn('mobile-submenu__item-inner', {
                'mobile-submenu__item-inner--active': selectedTab === tab.id,
              })}
            >
              {tab.label}
            </span>
          </DropDownMenuItem>
        </ShowCategory>
      );
    },
    [handleCloseAndChoose, selectedTab],
  );

  return (
    <>
      {menuItems.map((tab, i) => (
        <>
          {renderCustomMenuItem ? renderCustomMenuItem(tab) : renderMenuItem(tab, i)}
          {tab.subMenuItems?.map((tab) => (
            <ShowCategory key={tab.id} category={tab.label || ''}>
              <DropDownMenuItem
                className={cn('mobile-submenu__item', {
                  'mobile-submenu__item--active': selectedTab === tab.id,
                })}
                contentClassName={cn('mobile-submenu__item-content')}
                onClick={() => handleSubMenuCloseAndChoose(tab)}
                badge={tab.badge}
                autoFocus={i === 0}
                to={tab.to}
              >
                <span
                  className={cn('mobile-submenu__item-inner', {
                    'mobile-submenu__item-inner--active': selectedTab === tab.id,
                  })}
                >
                  {tab.label}
                </span>
              </DropDownMenuItem>
            </ShowCategory>
          ))}
        </>
      ))}
    </>
  );
}
